/**
 * browser-check.js — DIAGNOSTIC, TEMPORARY (customer-facing)
 * ----------------------------------------------------------------------------
 * Dual-mode, for the Facebook in-app browser freeze investigation
 * (ClaudeDevPlans/ToDo/FB-INAPP-FREEZE-INVESTIGATION.md).
 *
 * Loaded SITE-WIDE from layout/theme.liquid. Two modes, either or both may run:
 *
 *  1. REPORT — on /pages/browser-check only (detected by the [data-browser-check]
 *     mount): builds a snapshot and shows it for the customer to copy/paste.
 *
 *  2. BEACON — only when a diagnostic token is present (?diag=… in the URL, or
 *     saved in localStorage from an earlier tokened visit): sends the same snapshot
 *     to a cross-origin Cloudflare Worker on page load, +2.5s, and on pagehide.
 *     This is the ONLY way to capture the state in the run-up to a freeze: the
 *     freeze blocks any request to the Lamour domain, but a cross-origin request
 *     carries none of the Lamour cookies and still gets through. NO TOKEN => NO
 *     BEACON, so every ordinary visitor sends nothing.
 *
 * Privacy: reads and reports ONLY technical names and sizes — cookie names (never
 * values), localStorage key names (never values), external hostnames, the
 * user-agent, and total sizes. The beacon transmits that same safe snapshot, and
 * ONLY for the tokened, opted-in customer. Nothing is installed; nothing persists
 * after the visit beyond the saved token.
 * 2026-08-12: the beacon also sends navigation type + TTFB, JS heap size, a
 * total-storage estimate, visibility state, and ms since the previous beacon —
 * all numbers/enums, so the names-and-sizes-only promise is unchanged. The
 * customer-facing report on /pages/browser-check is deliberately untouched.
 *
 * Delete this file + the DiagnosticBeacon worker + the theme.liquid <script> line
 * + section + template + custom.css region when the bug closes.
 * ----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // --- config -------------------------------------------------------------
  var BEACON_URL = 'https://lamour-diagnostic-beacon.brucenconolly.workers.dev/';
  var TOKEN_PARAM = 'diag';
  var TOKEN_STORE_KEY = 'lamour-diag-token';
  var COOKIE_DANGER = 16 * 1024; // navigation dies past ~16KB cookie header (measured 2026-07-23)

  // --- token: URL ?diag=… wins, else a token saved from a previous visit ---
  function getToken() {
    var token = '';
    try {
      var m = location.search.match(new RegExp('[?&]' + TOKEN_PARAM + '=([^&]+)'));
      if (m) token = decodeURIComponent(m[1]);
    } catch (e) { /* ignore */ }
    try {
      if (token) localStorage.setItem(TOKEN_STORE_KEY, token);
      else token = localStorage.getItem(TOKEN_STORE_KEY) || '';
    } catch (e) { /* localStorage may throw in private mode */ }
    return token;
  }

  var root = document.querySelector('[data-browser-check]');
  var token = getToken();

  // Ordinary visitor on an ordinary page — do nothing at all.
  if (!root && !token) return;

  var reportEl = root ? root.querySelector('[data-bc-report]') : null;

  // --- helpers ------------------------------------------------------------
  function fmt(bytes) {
    if (bytes > 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    if (bytes > 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  }

  // --- data collectors (structured — used by BOTH report and beacon) ------
  function collectCookies() {
    var items = document.cookie.split(';')
      .map(function (c) {
        var eq = c.indexOf('=');
        return { name: c.slice(0, eq).trim(), size: c.length };
      })
      .filter(function (c) { return c.name; })
      .sort(function (a, b) { return b.size - a.size; });
    return { total: document.cookie.length, items: items };
  }

  function collectStorage() {
    var items = [];
    var total = 0;
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        var size = (k.length + (localStorage.getItem(k) || '').length) * 2;
        items.push({ name: k, size: size });
        total += size;
      }
    } catch (e) {
      return { total: 0, items: [], error: (e && e.name) || 'error' };
    }
    items.sort(function (a, b) { return b.size - a.size; });
    return { total: total, items: items };
  }

  // --- extra beacon-only signals (added 2026-08-12) -----------------------
  // Beacon payload only — the customer-facing report on /pages/browser-check is
  // deliberately unchanged. All numbers/enums, no values: the names-and-sizes-only
  // promise above still holds. Each collector is independently try/catch'd and
  // returns undefined on failure — a new signal must NEVER be able to stop the
  // cookie/storage/host capture, which is the only frozen-state record we get.

  // Navigation type + timing. `reload` bursts are her RETRYING a stalled page —
  // the fingerprint of a near-freeze she recovered from (see the 96s gap in the
  // 2026-08-10 session, which this would have explained outright).
  function collectNav() {
    try {
      var e = performance.getEntriesByType('navigation')[0];
      if (!e) return undefined;
      return {
        type: e.type,                        // navigate | reload | back_forward | prerender
        ttfb: Math.round(e.responseStart),   // ms to first byte — climbing = edge slowing for her
        size: e.transferSize                 // 0 => served from cache/bfcache, no network
      };
    } catch (err) { return undefined; }
  }

  // JS heap. Chromium-only, so this reports on HER Android and stays undefined on
  // iOS — every "webview wedge" theory predicts this climbing before a freeze.
  function collectHeap() {
    try {
      var m = performance.memory;
      if (!m) return undefined;
      return { used: m.usedJSHeapSize, lim: m.jsHeapSizeLimit };
    } catch (err) { return undefined; }
  }

  // Total bytes across ALL storage types — IndexedDB, CacheStorage, service worker
  // caches — none of which collectStorage() can see. This is the documented blind
  // spot where Klaviyo growth could resume with no alarm.
  // Async, so it is SAMPLED and cached: sendBeacon on pagehide cannot await a
  // Promise, so a send never waits on this; it carries the last known value.
  var storageEst;
  function sampleStorageEstimate() {
    try {
      if (!navigator.storage || !navigator.storage.estimate) return;
      navigator.storage.estimate().then(function (r) {
        storageEst = { use: r.usage, quota: r.quota };
      }, function () { /* ignore */ });
    } catch (err) { /* ignore */ }
  }

  // ms since this session's previous beacon. Silence in the data is currently
  // ambiguous (backgrounded app vs hung page); paired with `vis` below it stops
  // being a guess. Side-effecting — call exactly once per send.
  var GAP_KEY = 'lamour-diag-last';
  function readGap() {
    try {
      var prev = parseInt(sessionStorage.getItem(GAP_KEY) || '', 10);
      var now = Date.now();
      sessionStorage.setItem(GAP_KEY, String(now));
      return prev ? now - prev : undefined;
    } catch (err) { return undefined; }
  }

  function collectHosts() {
    var counts = {};
    try {
      performance.getEntriesByType('resource').forEach(function (r) {
        try {
          var h = new URL(r.name).hostname;
          if (h && h !== location.hostname) counts[h] = (counts[h] || 0) + 1;
        } catch (err) { /* ignore unparseable entries */ }
      });
    } catch (e) { /* performance API missing */ }
    return Object.keys(counts).sort().map(function (h) {
      return { host: h, count: counts[h] };
    });
  }

  function snapshot() {
    return {
      t: new Date().toISOString(),
      ua: navigator.userAgent,
      cookies: collectCookies(),
      storage: collectStorage(),
      hosts: collectHosts()
    };
  }

  // --- REPORT mode: one single-line, copy-friendly report -----------------
  function buildReport(s) {
    var cookieVerdict = s.cookies.total > COOKIE_DANGER
      ? '⚠️ VERY LARGE — this is the kind of load that breaks the site'
      : '✓ a normal amount';
    var lines = [
      'LAMOUR ARTISANS',
      'Ran: ' + s.t,
      '— BROWSER —',
      s.ua,
      '— COOKIES —',
      'Total: ' + s.cookies.items.length + ' cookies, ' + fmt(s.cookies.total) + ' — ' + cookieVerdict
    ];
    if (!s.cookies.items.length) lines.push('(none)');
    s.cookies.items.forEach(function (c) { lines.push(c.name + ' — ' + fmt(c.size)); });

    lines.push('— STORED SETTINGS —');
    if (s.storage.error) {
      lines.push('(not readable in this browser: ' + s.storage.error + ')');
    } else {
      lines.push('Total: ' + s.storage.items.length + ' items, ' + fmt(s.storage.total));
      if (!s.storage.items.length) lines.push('(none)');
      s.storage.items.forEach(function (it) { lines.push(it.name + ' — ' + fmt(it.size)); });
    }

    lines.push('— SERVICES THIS PAGE CONTACTED —');
    if (!s.hosts.length) lines.push('(none — everything came from ' + location.hostname + ')');
    s.hosts.forEach(function (h) { lines.push(h.host + ' — ' + h.count + ' request(s)'); });

    return lines.join(' | ');
  }

  function render() {
    if (reportEl) reportEl.textContent = buildReport(snapshot());
  }

  // --- BEACON mode: send the same snapshot cross-origin -------------------
  // Plain-text body => a "simple request", so NO CORS preflight (a preflight the
  // worker didn't answer would be dropped silently). We never read the response.
  function sendReport(event) {
    if (!token) return;
    var s = snapshot();
    var payload = JSON.stringify({
      token: token,
      event: event,
      t: s.t,
      page: location.pathname,   // which page she was on (path only — no query, no PII)
      ua: s.ua,
      cookieTotal: s.cookies.total,
      cookies: s.cookies.items.map(function (c) { return { n: c.name, s: c.size }; }),
      storage: s.storage.error
        ? { error: s.storage.error }
        : s.storage.items.map(function (it) { return { n: it.name, s: it.size }; }),
      hosts: s.hosts.map(function (h) { return { h: h.host, c: h.count }; }),
      // Added 2026-08-12. JSON.stringify drops undefined keys, so an unsupported
      // signal costs nothing on the wire.
      nav: collectNav(),
      heap: collectHeap(),
      storageEst: storageEst,
      vis: document.visibilityState,
      gap: readGap()
    });
    try {
      if (navigator.sendBeacon &&
          navigator.sendBeacon(BEACON_URL, new Blob([payload], { type: 'text/plain' }))) {
        return;
      }
    } catch (e) { /* fall through to fetch */ }
    try {
      fetch(BEACON_URL, {
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
        mode: 'no-cors'
      });
    } catch (e) { /* nothing more we can do */ }
  }

  // --- copy button (report mode only) -------------------------------------
  if (root) {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bc-copy]');
      if (!btn || !reportEl) return;
      var text = reportEl.textContent;
      function done(ok) {
        btn.textContent = ok ? '✓ Copied — now paste it in a message' : 'Copy failed — please screenshot instead';
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });
      } else {
        var ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { done(document.execCommand('copy')); } catch (err) { done(false); }
        document.body.removeChild(ta);
      }
    });
  }

  // --- run ----------------------------------------------------------------
  sampleStorageEstimate(); // kick off first (async) — the 'load' send won't have it
                           // yet, 'late' and 'pagehide' will.
  render();               // first paint of the on-page report (if any)
  sendReport('load');     // first beacon (if tokened)

  window.addEventListener('load', function () {
    setTimeout(function () {
      render();           // re-render once late trackers have landed
      sampleStorageEstimate();  // refresh so 'pagehide' carries a current figure
      sendReport('late');
    }, 2500);
  });

  // Last observable state before leaving this page — catches cookie/storage
  // growth that happens mid-visit, where the NEXT page's on-load send (the
  // navigation that dies) never runs.
  window.addEventListener('pagehide', function () { sendReport('pagehide'); });
})();
